/**
 * Demo mode: intercepts fetch() for /api/* routes and returns in-memory mock
 * data so the UI is fully browsable without a running database.
 *
 * Enabled when VITE_DEMO_MODE=1. Install by calling installDemoApi() ONCE in
 * main.tsx before the app mounts.
 *
 * All data is in-memory and resets on page refresh. This is a preview/demo
 * affordance only — it has zero relationship to the real API and must never
 * be compiled into a production bundle.
 */

import type { ZodError } from "zod";
import { calculateAge } from "@shared/lib/age";
import {
  HEALTH_REMINDERS_ENABLED,
  PHOTO_UPLOADS_ENABLED,
  PICKUP_CONSENT_ENABLED,
} from "@shared/constants/features";
import { effectiveVisibleSections } from "@shared/lib/visibleSections";
import {
  createImmunizationSchema,
  updateImmunizationSchema,
} from "@shared/validation/immunization.schema";
import {
  createAuthorizedPickupSchema,
  updateAuthorizedPickupSchema,
} from "@shared/validation/authorizedPickup.schema";
import { upsertMedicalHistorySchema } from "@shared/validation/medicalHistory.schema";
import { createRoutineSchema, updateRoutineSchema } from "@shared/validation/routine.schema";
import { upsertFeedingSchema } from "@shared/validation/feeding.schema";
import type { RoutineSummary } from "./useRoutines";
import type { FeedingProfileData } from "./useFeeding";
import { upsertCareAuthorizationSchema } from "@shared/validation/careAuthorization.schema";
import type { ImmunizationSummary } from "./useImmunizations";
import type { AuthorizedPickupSummary } from "./useAuthorizedPickups";
import type { MedicalHistoryData } from "./useMedicalHistory";
import type { CareAuthorizationData } from "./useCareAuthorization";
import { isNotificationKindVisible } from "@shared/constants/notifications";
import type { DashboardLink, DashboardLinkStatus } from "@shared/types/dashboard";
import { accountRegionSchema } from "@shared/validation/accountRegion.schema";
import { createBetaFeedbackSchema } from "@shared/validation/betaFeedback.schema";
import { handleFeatureMock } from "../../features/_sim/mockRouter"; // SIM-SEAM: v2 feature mocks
import { getSimContext, setSimContext } from "../../features/_sim/context"; // SIM-SEAM: region = the Sim Panel's market

/**
 * Mirrors the real API's child response: age is computed from dateOfBirth,
 * never stored; there is never a `photoUrl` (PHOTO_UPLOADS_ENABLED is off, and
 * the demo store holds no photos at all), only `hasPhoto: false`.
 */
function withComputedAge(
  child: DemoChild,
): DemoChild & { age: number | null; hasPhoto: boolean } {
  return {
    ...child,
    age: child.dateOfBirth ? calculateAge(child.dateOfBirth) : null,
    hasPhoto: false,
  };
}

/** The real server answers a child body carrying `photoUrl` with a plain 404. */
function carriesPhoto(body: unknown): boolean {
  return typeof body === "object" && body !== null && "photoUrl" in body;
}

interface DemoChild {
  id: string;
  /** The nickname (what caregivers call them). */
  name: string;
  /** Optional legal name; hidden on care links unless the link opts in. */
  legalName: string | null;
  dateOfBirth: string | null;
  grade: string | null;
  additionalNotes: string | null;
  isEnrolled: boolean;
  schoolName: string | null;
  schoolPhone: string | null;
  teacherName: string | null;
  schoolNursePhone: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  emergencyContactRelationship: string | null;
  secondaryEmergencyContactName: string | null;
  secondaryEmergencyContactPhone: string | null;
  secondaryEmergencyContactRelationship: string | null;
  createdAt: string;
  updatedAt: string;
}

type DemoSeverity = "mild" | "moderate" | "severe";
type DemoFrequency =
  | "as_needed"
  | "once_daily"
  | "twice_daily"
  | "three_times_daily"
  | "weekly"
  | "monthly";

interface DemoAllergy {
  id: string;
  childId: string;
  name: string;
  severity: DemoSeverity;
  description: string | null;
  emergencyAction: string | null;
  reactionSymptoms: string | null;
  treatment: string | null;
  epinephrineLocation: string | null;
  rescueMedicationId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DemoMedication {
  id: string;
  childId: string;
  name: string;
  dosage: string;
  frequency: DemoFrequency;
  expiresAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DemoCarePreferences {
  id: string;
  childId: string;
  behavioralNotes: string | null;
  calmingStrategies: string | null;
  comfortItems: string | null;
  communicationStyle: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DemoVisibleSections {
  basic: boolean;
  emergencyContacts: boolean;
  allergies: boolean;
  medications: boolean;
  carePreferences: boolean;
  /** Optional on purpose: a link without the key keeps the legal name hidden. */
  legalName?: boolean;
  /** Sent by the share dialog; stored false and listed absent while Pickup & consent is off. */
  pickup?: boolean;
}

interface DemoSharedLink {
  id: string;
  childId: string;
  token: string; // stored plaintext ONLY in demo mode — real server HMACs it
  tokenPrefix: string;
  caregiverName: string;
  caregiverEmail: string | null;
  visibleSections: DemoVisibleSections;
  expiresAt: string | null;
  isActive: boolean;
  lastAccessedAt: string | null;
  accessCount: number;
  createdAt: string;
  revokedAt: string | null;
  acknowledgedAt: string | null;
  /** Demo-store only, like `token`: the real list DTO never carries it. */
  dismissedAt: string | null;
}

// Mirrors NotificationDTO from @shared/types/notifications (kept inline so this
// demo file stays import-free and self-contained, like the other Demo* types).
interface DemoNotification {
  id: string;
  kind:
    | "link_opened"
    | "link_unacknowledged"
    | "medication_expiring"
    | "allergy_plan_incomplete";
  title: string;
  body: string;
  linkPath: string | null;
  readAt: string | null;
  createdAt: string;
  relatedChildId: string | null;
}

const DEMO_USER = {
  id: "demo-user",
  email: "demo@leenaslist.local",
  fullName: "Demo Parent",
  // Demo mode has no server, so two-factor cannot be exercised — report it as
  // unavailable rather than enabled-but-broken.
  mfaEnabled: false,
  mfaAvailable: false,
  mfaBackupCodesRemaining: 0,
  // Pre-accepted: the demo is a browsable preview, so the terms gate would
  // just be a wall in front of it.
  termsAcceptedAt: new Date("2026-04-01T00:00:00Z").toISOString(),
  createdAt: new Date("2026-04-01T00:00:00Z").toISOString(),
  updatedAt: new Date("2026-04-01T00:00:00Z").toISOString(),
};

const now = () => new Date().toISOString();

const demoAllergies: DemoAllergy[] = [
  {
    id: "demo-allergy-1",
    childId: "demo-child-1",
    name: "Peanuts",
    severity: "severe",
    description: "Anaphylactic reaction to all peanut products, including trace amounts.",
    emergencyAction: "Notify parents as soon as she is stable.",
    reactionSymptoms:
      "Hives around the mouth, swelling of the lips or tongue, coughing, wheezing, trouble breathing.",
    treatment:
      "Use the epinephrine auto-injector FIRST, then call 911. Do not wait to see if antihistamines work.",
    epinephrineLocation: "Blue medical pouch in the front of her backpack.",
    rescueMedicationId: "demo-med-3",
    notes: "A second auto-injector is kept in the school nurse's office.",
    createdAt: new Date("2026-04-01T12:10:00Z").toISOString(),
    updatedAt: new Date("2026-04-01T12:10:00Z").toISOString(),
  },
  {
    id: "demo-allergy-2",
    childId: "demo-child-1",
    name: "Dairy",
    severity: "moderate",
    description: "Lactose intolerance. Causes stomach pain and discomfort within 30 minutes.",
    emergencyAction: null,
    reactionSymptoms: "Stomach pain and discomfort within about 30 minutes.",
    treatment: "Stop the food. Offer water. No medication needed.",
    epinephrineLocation: null,
    rescueMedicationId: null,
    notes: "Lactose-free milk and butter are OK.",
    createdAt: new Date("2026-04-01T12:11:00Z").toISOString(),
    updatedAt: new Date("2026-04-01T12:11:00Z").toISOString(),
  },
  {
    id: "demo-allergy-3",
    childId: "demo-child-1",
    name: "Pollen (seasonal)",
    severity: "mild",
    description: "Mild hay fever in spring.",
    emergencyAction: null,
    reactionSymptoms: "Sneezing and itchy eyes.",
    treatment: "Antihistamine as needed.",
    epinephrineLocation: null,
    rescueMedicationId: null,
    notes: "Benadryl as needed.",
    createdAt: new Date("2026-04-01T12:12:00Z").toISOString(),
    updatedAt: new Date("2026-04-01T12:12:00Z").toISOString(),
  },
];

const demoCarePreferences: DemoCarePreferences[] = [
  {
    id: "demo-care-1",
    childId: "demo-child-1",
    behavioralNotes:
      "Gets overwhelmed in loud environments. Tends to seek out quiet corners when overstimulated.",
    calmingStrategies:
      "Deep pressure from a weighted blanket usually works. Counting backwards from 10 while she takes slow breaths. Talking about dinosaurs is a reliable distraction.",
    comfortItems:
      "Stuffed T-Rex named 'Rexy' (in her backpack), weighted blanket (in emergency bag), noise-canceling headphones for crowded places.",
    communicationStyle:
      "Verbal and very articulate. Responds best to clear, direct language. Avoid sarcasm — she takes things literally. Ask specific yes/no questions when she's upset.",
    createdAt: new Date("2026-04-01T12:20:00Z").toISOString(),
    updatedAt: new Date("2026-04-01T12:20:00Z").toISOString(),
  },
];

const demoMedications: DemoMedication[] = [
  {
    id: "demo-med-1",
    childId: "demo-child-1",
    name: "Albuterol Inhaler",
    dosage: "2 puffs",
    frequency: "as_needed",
    expiresAt: new Date("2027-06-15T23:59:59Z").toISOString(),
    notes: "For asthma during physical activity. Shake before use.",
    createdAt: new Date("2026-04-01T12:15:00Z").toISOString(),
    updatedAt: new Date("2026-04-01T12:15:00Z").toISOString(),
  },
  {
    id: "demo-med-3",
    childId: "demo-child-1",
    name: "EpiPen Jr Auto-Injector",
    dosage: "0.15mg",
    frequency: "as_needed",
    expiresAt: new Date("2027-03-20T23:59:59Z").toISOString(),
    notes: "Rescue device for the peanut allergy. Two units: backpack + nurse's office.",
    createdAt: new Date("2026-04-01T12:14:00Z").toISOString(),
    updatedAt: new Date("2026-04-01T12:14:00Z").toISOString(),
  },
  {
    id: "demo-med-2",
    childId: "demo-child-1",
    name: "Children's Claritin",
    dosage: "5ml",
    frequency: "once_daily",
    expiresAt: new Date("2026-09-01T23:59:59Z").toISOString(),
    notes: "For seasonal allergies, April through June.",
    createdAt: new Date("2026-04-01T12:16:00Z").toISOString(),
    updatedAt: new Date("2026-04-01T12:16:00Z").toISOString(),
  },
];

// Nickname first (BRIEF §5.2): Maya has an optional legal name on file, Theo
// doesn't, so the demo shows both states. Neither has a photo (BRIEF §5.3).
const demoChildren: DemoChild[] = [
  {
    id: "demo-child-1",
    name: "Maya",
    legalName: "Maya Sofia Rivera",
    dateOfBirth: "2019-03-14",
    grade: "2nd grade",
    additionalNotes: "Loves dinosaurs. Can get overwhelmed in loud environments.",
    isEnrolled: true,
    schoolName: null, // no school names in sample content (promise 3)
    schoolPhone: "555-0100",
    teacherName: "Ms. Alvarez",
    schoolNursePhone: "555-0102",
    emergencyContactName: "Jordan Rivera",
    emergencyContactPhone: "555-0101",
    emergencyContactRelationship: "Father",
    secondaryEmergencyContactName: "Sam Chen",
    secondaryEmergencyContactPhone: "555-0102",
    secondaryEmergencyContactRelationship: "Grandmother",
    createdAt: new Date("2026-04-01T12:00:00Z").toISOString(),
    updatedAt: new Date("2026-04-01T12:00:00Z").toISOString(),
  },
  {
    id: "demo-child-2",
    name: "Theo",
    legalName: null,
    dateOfBirth: "2023-06-02",
    grade: null,
    additionalNotes: null,
    isEnrolled: true,
    schoolName: null,
    schoolPhone: "555-0200",
    teacherName: null,
    schoolNursePhone: null,
    emergencyContactName: "Jordan Rivera",
    emergencyContactPhone: "555-0101",
    emergencyContactRelationship: "Father",
    secondaryEmergencyContactName: null,
    secondaryEmergencyContactPhone: null,
    secondaryEmergencyContactRelationship: null,
    createdAt: new Date("2026-04-01T12:05:00Z").toISOString(),
    updatedAt: new Date("2026-04-01T12:05:00Z").toISOString(),
  },
];

function jsonResponse(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

function successEnvelope<T>(data: T) {
  return { ok: true, data };
}

function errorEnvelope(code: string, message: string) {
  return { ok: false, error: { code, message } };
}

function randomId(): string {
  return "demo-" + Math.random().toString(36).slice(2, 10);
}

function randomToken(): string {
  const alphabet =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-";
  let s = "";
  for (let i = 0; i < 32; i++) {
    s += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return s;
}

const demoSharedLinks: DemoSharedLink[] = [
  {
    id: "demo-link-1",
    childId: "demo-child-1",
    token: "demoShareTokenForMayaDemoShareT00",
    tokenPrefix: "demoSh",
    caregiverName: "Sarah Johnson (Babysitter)",
    caregiverEmail: "sarah@example.com",
    visibleSections: {
      basic: true,
      emergencyContacts: true,
      allergies: true,
      medications: true,
      carePreferences: true,
    },
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    isActive: true,
    lastAccessedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    accessCount: 3,
    createdAt: new Date("2026-04-12T10:00:00Z").toISOString(),
    revokedAt: null,
    acknowledgedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    dismissedAt: null,
  },
  {
    id: "demo-link-2",
    childId: "demo-child-1",
    token: "demoShareTokenForNurseMayaDemo123",
    tokenPrefix: "demoSh",
    caregiverName: "Ms. Rivera (School Nurse)",
    caregiverEmail: null,
    visibleSections: {
      basic: true,
      emergencyContacts: true,
      allergies: true,
      medications: true,
      carePreferences: false,
      // A school nurse is exactly who may need the legal name. The babysitter
      // link above has no `legalName` key at all, like a pre-v2 link: hidden.
      legalName: true,
    },
    expiresAt: null,
    isActive: true,
    lastAccessedAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    accessCount: 12,
    createdAt: new Date("2026-04-05T08:00:00Z").toISOString(),
    revokedAt: null,
    acknowledgedAt: null,
    dismissedAt: null,
  },
  // The rest give the Active links popup one of each status to show.
  {
    id: "demo-link-3",
    childId: "demo-child-2",
    token: "demoShareTokenForTheoGrandmaDemo1",
    tokenPrefix: "demoSh",
    caregiverName: "Grandma Rosa",
    caregiverEmail: null,
    visibleSections: {
      basic: true,
      emergencyContacts: true,
      allergies: true,
      medications: false,
      carePreferences: true,
    },
    expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    isActive: true,
    lastAccessedAt: null,
    accessCount: 0,
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    revokedAt: null,
    acknowledgedAt: null,
    dismissedAt: null,
  },
  {
    id: "demo-link-4",
    childId: "demo-child-1",
    token: "demoShareTokenForMayaWeekendSit01",
    tokenPrefix: "demoSh",
    caregiverName: "Weekend sitter (Jess)",
    caregiverEmail: null,
    visibleSections: {
      basic: true,
      emergencyContacts: true,
      allergies: true,
      medications: true,
      carePreferences: false,
    },
    expiresAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    isActive: true,
    lastAccessedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    accessCount: 2,
    createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    revokedAt: null,
    acknowledgedAt: null,
    dismissedAt: null,
  },
  {
    id: "demo-link-5",
    childId: "demo-child-2",
    token: "demoShareTokenForTheoSwimCoach01",
    tokenPrefix: "demoSh",
    caregiverName: "Swim coach (Daniel)",
    caregiverEmail: "daniel@example.com",
    visibleSections: {
      basic: true,
      emergencyContacts: true,
      allergies: false,
      medications: false,
      carePreferences: false,
    },
    expiresAt: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString(),
    isActive: false,
    lastAccessedAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    accessCount: 1,
    createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    revokedAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    acknowledgedAt: null,
    dismissedAt: null,
  },
];

/**
 * The list DTO the real API returns: never the token, never `dismissedAt`, and
 * (like toSharedLinkDto) only the sections the link opens today.
 */
function toLinkDto(link: DemoSharedLink) {
  const { token: _token, dismissedAt: _dismissedAt, ...rest } = link;
  return { ...rest, visibleSections: effectiveVisibleSections(rest.visibleSections) };
}

/** Same rule as the server's isLiveLink(): not cancelled and not past its expiry. */
function isLiveDemoLink(link: DemoSharedLink): boolean {
  return link.isActive && (link.expiresAt === null || Date.parse(link.expiresAt) > Date.now());
}

function demoLinkStatus(link: DemoSharedLink): DashboardLinkStatus {
  if (isLiveDemoLink(link)) return "active";
  return link.isActive ? "expired" : "cancelled";
}

/**
 * Mirrors GET /api/dashboard/links: not dismissed, child not deleted, active
 * first, then ended newest first.
 */
function demoDashboardLinks(): DashboardLink[] {
  const rows = demoSharedLinks
    .filter((l) => l.dismissedAt === null && demoChildren.some((c) => c.id === l.childId))
    .map(
      (l): DashboardLink => ({
        id: l.id,
        childId: l.childId,
        childName: demoChildren.find((c) => c.id === l.childId)?.name ?? "Your child",
        caregiverName: l.caregiverName,
        caregiverEmail: l.caregiverEmail,
        tokenPrefix: l.tokenPrefix,
        // The demo store predates the newer section keys; fill them so the
        // shape matches the real VisibleSections (a switched-off one left out,
        // as the server does).
        visibleSections: effectiveVisibleSections({
          routines: false,
          feeding: false,
          medical: false,
          pickup: false,
          ...l.visibleSections,
          legalName: l.visibleSections.legalName === true,
        }),
        status: demoLinkStatus(l),
        accessCount: l.accessCount,
        lastAccessedAt: l.lastAccessedAt,
        acknowledgedAt: l.acknowledgedAt,
        expiresAt: l.expiresAt,
        revokedAt: l.revokedAt,
        createdAt: l.createdAt,
      }),
    );
  const rank = (r: DashboardLink) => (r.status === "active" ? 0 : 1);
  const expiry = (r: DashboardLink) => (r.expiresAt ? Date.parse(r.expiresAt) : Number.POSITIVE_INFINITY);
  return [...rows].sort((a, b) => {
    if (rank(a) !== rank(b)) return rank(a) - rank(b);
    if (rank(a) === 0 && expiry(a) !== expiry(b)) return expiry(a) - expiry(b);
    return Date.parse(b.createdAt) - Date.parse(a.createdAt);
  });
}

// linkPath values are internal deep links only — a notification must never
// carry a /care/:token share URL (mirrors the server's linkPath sanitizer).
const demoNotifications: DemoNotification[] = [
  {
    id: "demo-notif-1",
    kind: "link_opened",
    title: "Sarah opened Maya's care link",
    body: "Sarah Johnson (Babysitter) viewed the shared profile.",
    linkPath: "/child/demo-child-1",
    readAt: null,
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    relatedChildId: "demo-child-1",
  },
  {
    id: "demo-notif-2",
    kind: "medication_expiring",
    title: "Children's Claritin expires soon",
    body: "Maya's Children's Claritin expires in 12 days. Consider a refill.",
    linkPath: "/child/demo-child-1",
    readAt: new Date(Date.now() - 20 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    relatedChildId: "demo-child-1",
  },
  {
    id: "demo-notif-3",
    kind: "link_unacknowledged",
    title: "School nurse hasn't acknowledged Maya's link",
    body: "Ms. Rivera (School Nurse) hasn't acknowledged the link after 48 hours.",
    linkPath: "/child/demo-child-1",
    readAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    relatedChildId: "demo-child-1",
  },
];

type DemoNotificationKind = DemoNotification["kind"];
interface DemoPreference {
  kind: DemoNotificationKind;
  inApp: boolean;
  email: boolean;
}
const DEMO_PREFERENCE_KINDS: string[] = [
  "link_opened",
  "link_unacknowledged",
  "allergy_plan_incomplete",
  "medication_expiring",
] satisfies DemoNotificationKind[];
/** In-memory per-kind toggles; a missing kind means both channels on. */
const demoPreferences: Partial<Record<string, { inApp: boolean; email: boolean }>> = {};

function readBody(init: RequestInit | undefined): unknown {
  if (!init?.body) return {};
  if (typeof init.body === "string") {
    try {
      return JSON.parse(init.body);
    } catch {
      return {};
    }
  }
  return {};
}

// ---------------------------------------------------------------------------
// Medical, routine, feeding, pickup and consent records: immunizations,
// medical history, routines, feeding & diet, authorized pickups and consent to
// treat (the Medical, Routines, Feeding and Pickup & Consent tabs, and the
// EIF). Full read/write mocks with the real API's response
// shapes (the DTOs in server/src/services/*.service.ts, typed here through the
// client hooks' interfaces), validated with the same shared Zod schemas and
// answering the same 400/404 envelopes. Every store starts empty, as for a
// child with no rows yet, so the EIF and caregiver demo data are unchanged.
// While PICKUP_CONSENT_ENABLED is off the pickup and consent paths answer the
// standard 404 instead, like the real server (their routes are not mounted).
// ---------------------------------------------------------------------------

const demoImmunizations: ImmunizationSummary[] = [];
const demoRoutines: RoutineSummary[] = [];
const demoFeeding = new Map<string, FeedingProfileData>();
const demoPickups: AuthorizedPickupSummary[] = [];
const demoMedicalHistory = new Map<string, MedicalHistoryData>();
const demoCareAuthorizations = new Map<string, CareAuthorizationData>();

function validationError(error: ZodError): Response {
  const message = error.issues[0]?.message ?? "Invalid request";
  return jsonResponse(errorEnvelope("VALIDATION_ERROR", message), 400);
}

/** Only the keys a parsed upsert actually set (the services skip `undefined`). */
function definedOnly<T extends object>(input: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
}

/** Mirrors refusePickupPhoto: a non-empty photo answers the standard 404 while uploads are off. */
function refusesPickupPhoto(body: unknown): boolean {
  const photo = (body as { photoUrl?: unknown } | null)?.photoUrl;
  return !PHOTO_UPLOADS_ENABLED && typeof photo === "string" && photo.trim().length > 0;
}

function handleChildRecordsMock(
  url: string,
  method: string,
  init: RequestInit | undefined,
): Response | null {
  const notFound = (what: string) => jsonResponse(errorEnvelope("NOT_FOUND", `${what} not found`), 404);
  const childExists = (childId: string) => demoChildren.some((c) => c.id === childId);

  // Pickup & consent switched off: the real server does not mount either route,
  // so every method on them answers the standard 404 envelope, as here.
  if (
    !PICKUP_CONSENT_ENABLED &&
    (/^\/api\/children\/[^/]+\/authorized-pickups(\/[^/]+)?$/.test(url) ||
      /^\/api\/care-authorizations\/[^/]+$/.test(url))
  ) {
    return jsonResponse(errorEnvelope("NOT_FOUND", "Not found"), 404);
  }

  // Immunizations: a list per child, newest first.
  const immListMatch = url.match(/^\/api\/children\/([^/]+)\/immunizations$/);
  if (immListMatch) {
    const childId = immListMatch[1]!;
    if (!childExists(childId)) return notFound("Child");
    if (method === "GET") {
      return jsonResponse(successEnvelope(demoImmunizations.filter((i) => i.childId === childId)));
    }
    if (method === "POST") {
      const parsed = createImmunizationSchema.safeParse(readBody(init));
      if (!parsed.success) return validationError(parsed.error);
      const created: ImmunizationSummary = {
        id: randomId(),
        childId,
        vaccineName: parsed.data.vaccineName,
        dateAdministered: parsed.data.dateAdministered ?? null,
        nextDueDate: parsed.data.nextDueDate ?? null,
        provider: parsed.data.provider ?? null,
        notes: parsed.data.notes ?? null,
        createdAt: now(),
        updatedAt: now(),
      };
      demoImmunizations.unshift(created);
      return jsonResponse(successEnvelope(created), 201);
    }
  }
  const immItemMatch = url.match(/^\/api\/children\/([^/]+)\/immunizations\/([^/]+)$/);
  if (immItemMatch && (method === "PUT" || method === "DELETE")) {
    const [, childId, id] = immItemMatch;
    const index = demoImmunizations.findIndex((i) => i.id === id && i.childId === childId);
    if (index === -1) return notFound("Immunization");
    if (method === "DELETE") {
      demoImmunizations.splice(index, 1);
      return jsonResponse(successEnvelope({ deleted: true }));
    }
    const parsed = updateImmunizationSchema.safeParse(readBody(init));
    if (!parsed.success) return validationError(parsed.error);
    const prev = demoImmunizations[index]!;
    const updated: ImmunizationSummary = { ...prev, ...definedOnly(parsed.data), updatedAt: now() };
    demoImmunizations[index] = updated;
    return jsonResponse(successEnvelope(updated));
  }

  // Authorized pickups: a list per child, newest first, never with a photo.
  const pickupListMatch = url.match(/^\/api\/children\/([^/]+)\/authorized-pickups$/);
  if (pickupListMatch) {
    const childId = pickupListMatch[1]!;
    if (!childExists(childId)) return notFound("Child");
    if (method === "GET") {
      return jsonResponse(successEnvelope(demoPickups.filter((p) => p.childId === childId)));
    }
    if (method === "POST") {
      const raw = readBody(init);
      if (refusesPickupPhoto(raw)) return jsonResponse(errorEnvelope("NOT_FOUND", "Not found"), 404);
      const parsed = createAuthorizedPickupSchema.safeParse(raw);
      if (!parsed.success) return validationError(parsed.error);
      const created: AuthorizedPickupSummary = {
        id: randomId(),
        childId,
        name: parsed.data.name,
        relationship: parsed.data.relationship ?? null,
        phone: parsed.data.phone ?? null,
        isActive: parsed.data.isActive,
        notes: parsed.data.notes ?? null,
        createdAt: now(),
        updatedAt: now(),
      };
      demoPickups.unshift(created);
      return jsonResponse(successEnvelope(created), 201);
    }
  }
  const pickupItemMatch = url.match(/^\/api\/children\/([^/]+)\/authorized-pickups\/([^/]+)$/);
  if (pickupItemMatch && (method === "PUT" || method === "DELETE")) {
    const [, childId, id] = pickupItemMatch;
    const index = demoPickups.findIndex((p) => p.id === id && p.childId === childId);
    if (index === -1) return notFound("Authorized pickup");
    if (method === "DELETE") {
      demoPickups.splice(index, 1);
      return jsonResponse(successEnvelope({ deleted: true }));
    }
    const raw = readBody(init);
    if (refusesPickupPhoto(raw)) return jsonResponse(errorEnvelope("NOT_FOUND", "Not found"), 404);
    const parsed = updateAuthorizedPickupSchema.safeParse(raw);
    if (!parsed.success) return validationError(parsed.error);
    const { photoUrl: _photo, ...changes } = parsed.data;
    const prev = demoPickups[index]!;
    const updated: AuthorizedPickupSummary = { ...prev, ...definedOnly(changes), updatedAt: now() };
    demoPickups[index] = updated;
    return jsonResponse(successEnvelope(updated));
  }

  // Routines: a list per child, in sortOrder then creation order.
  const routineListMatch = url.match(/^\/api\/children\/([^/]+)\/routines$/);
  if (routineListMatch) {
    const childId = routineListMatch[1]!;
    if (!childExists(childId)) return notFound("Child");
    if (method === "GET") {
      const rows = demoRoutines
        .filter((r) => r.childId === childId)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt));
      return jsonResponse(successEnvelope(rows));
    }
    if (method === "POST") {
      const parsed = createRoutineSchema.safeParse(readBody(init));
      if (!parsed.success) return validationError(parsed.error);
      const created: RoutineSummary = {
        id: randomId(),
        childId,
        label: parsed.data.label,
        daypart: parsed.data.daypart,
        category: parsed.data.category,
        timeText: parsed.data.timeText ?? null,
        details: parsed.data.details ?? null,
        sortOrder: parsed.data.sortOrder ?? 0,
        notes: parsed.data.notes ?? null,
        createdAt: now(),
        updatedAt: now(),
      };
      demoRoutines.push(created);
      return jsonResponse(successEnvelope(created), 201);
    }
  }
  const routineItemMatch = url.match(/^\/api\/children\/([^/]+)\/routines\/([^/]+)$/);
  if (routineItemMatch && (method === "PUT" || method === "DELETE")) {
    const [, childId, id] = routineItemMatch;
    const index = demoRoutines.findIndex((r) => r.id === id && r.childId === childId);
    if (index === -1) return notFound("Routine");
    if (method === "DELETE") {
      demoRoutines.splice(index, 1);
      return jsonResponse(successEnvelope({ deleted: true }));
    }
    const parsed = updateRoutineSchema.safeParse(readBody(init));
    if (!parsed.success) return validationError(parsed.error);
    const prev = demoRoutines[index]!;
    const updated: RoutineSummary = { ...prev, ...definedOnly(parsed.data), updatedAt: now() };
    demoRoutines[index] = updated;
    return jsonResponse(successEnvelope(updated));
  }

  // Feeding & diet: one record per child, or null before the first save.
  const feedingMatch = url.match(/^\/api\/feeding\/([^/]+)$/);
  if (feedingMatch && (method === "GET" || method === "PUT")) {
    const childId = feedingMatch[1]!;
    if (!childExists(childId)) return notFound("Child");
    const existing = demoFeeding.get(childId) ?? null;
    if (method === "GET") return jsonResponse(successEnvelope(existing));
    const parsed = upsertFeedingSchema.safeParse(readBody(init));
    if (!parsed.success) return validationError(parsed.error);
    const base: FeedingProfileData = existing ?? {
      id: randomId(),
      childId,
      dietType: null,
      restrictions: null,
      likes: null,
      dislikes: null,
      portionNotes: null,
      scheduleNotes: null,
      bottleFormula: null,
      utensilsNotes: null,
      createdAt: now(),
      updatedAt: now(),
    };
    const saved: FeedingProfileData = { ...base, ...definedOnly(parsed.data), updatedAt: now() };
    demoFeeding.set(childId, saved);
    return jsonResponse(successEnvelope(saved));
  }

  // Medical history: one record per child, or null before the first save.
  const historyMatch = url.match(/^\/api\/medical-history\/([^/]+)$/);
  if (historyMatch && (method === "GET" || method === "PUT")) {
    const childId = historyMatch[1]!;
    if (!childExists(childId)) return notFound("Child");
    const existing = demoMedicalHistory.get(childId) ?? null;
    if (method === "GET") return jsonResponse(successEnvelope(existing));
    const parsed = upsertMedicalHistorySchema.safeParse(readBody(init));
    if (!parsed.success) return validationError(parsed.error);
    const base: MedicalHistoryData = existing ?? {
      id: randomId(),
      childId,
      primaryPhysicianName: null,
      primaryPhysicianPhone: null,
      dentistName: null,
      dentistPhone: null,
      bloodType: null,
      conditions: null,
      pastIllnesses: null,
      hospitalizations: null,
      insuranceNotes: null,
      notes: null,
      createdAt: now(),
      updatedAt: now(),
    };
    const saved: MedicalHistoryData = { ...base, ...definedOnly(parsed.data), updatedAt: now() };
    demoMedicalHistory.set(childId, saved);
    return jsonResponse(successEnvelope(saved));
  }

  // Consent to treat + insurance: one record per child; the grant time is
  // stamped here, never taken from the request (CareAuthorizationsService.save).
  const consentMatch = url.match(/^\/api\/care-authorizations\/([^/]+)$/);
  if (consentMatch && (method === "GET" || method === "PUT")) {
    const childId = consentMatch[1]!;
    if (!childExists(childId)) return notFound("Child");
    const existing = demoCareAuthorizations.get(childId) ?? null;
    if (method === "GET") return jsonResponse(successEnvelope(existing));
    const parsed = upsertCareAuthorizationSchema.safeParse(readBody(init));
    if (!parsed.success) return validationError(parsed.error);
    const base: CareAuthorizationData = existing ?? {
      id: randomId(),
      childId,
      consentToTreat: false,
      consentGrantedByName: null,
      consentGrantedAt: null,
      insuranceCarrier: null,
      insurancePolicyNumber: null,
      insuranceGroupNumber: null,
      preferredHospital: null,
      notes: null,
      createdAt: now(),
      updatedAt: now(),
    };
    const changes = definedOnly(parsed.data);
    let consentGrantedAt = base.consentGrantedAt;
    if (changes.consentToTreat !== undefined) {
      consentGrantedAt = changes.consentToTreat
        ? existing?.consentToTreat
          ? (existing.consentGrantedAt ?? now())
          : now()
        : null;
    }
    const saved: CareAuthorizationData = { ...base, ...changes, consentGrantedAt, updatedAt: now() };
    demoCareAuthorizations.set(childId, saved);
    return jsonResponse(successEnvelope(saved));
  }

  return null;
}

/**
 * `?scenario=empty`: a brand-new account (no children, care links, records or
 * alerts), to preview the fresh-account dashboard. Read once at boot, like the
 * other sim parameters; the season scenario ignores the unknown id and stays
 * "normal".
 */
function startAsNewAccount(): void {
  if (new URLSearchParams(window.location.search).get("scenario") !== "empty") return;
  const lists: unknown[][] = [
    demoChildren,
    demoSharedLinks,
    demoNotifications,
    demoAllergies,
    demoMedications,
    demoCarePreferences,
  ];
  for (const list of lists) list.splice(0, list.length);
}

export function installDemoApi(): void {
  if (!import.meta.env.VITE_DEMO_MODE) return;
  startAsNewAccount();

  const realFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;

    const method = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();

    // Only intercept our own API routes; pass through Vite HMR, asset reqs, etc.
    if (!url.startsWith("/api")) {
      return realFetch(input, init);
    }

    // Pass the real server's /api/health through so the landing page still shows live data.
    if (url.startsWith("/api/health")) {
      return realFetch(input, init);
    }

    // SIM-SEAM: v2 simulation features answer their own /api paths first.
    const simResponse = await handleFeatureMock(url, method, init);
    if (simResponse) return simResponse;

    // --- Auth ---
    if (url === "/api/auth/user" && method === "GET") {
      return jsonResponse(successEnvelope(DEMO_USER));
    }
    if (url === "/api/auth/logout" && method === "POST") {
      return jsonResponse(successEnvelope({ loggedOut: true }));
    }
    if (url === "/api/auth/accept-terms" && method === "POST") {
      return jsonResponse(successEnvelope({ termsAcceptedAt: DEMO_USER.termsAcceptedAt }));
    }
    if (url === "/api/auth/login" && method === "POST") {
      return jsonResponse(successEnvelope(DEMO_USER));
    }
    if (url === "/api/auth/register" && method === "POST") {
      return jsonResponse(successEnvelope(DEMO_USER), 201);
    }

    // --- Region ("Where is your family based?") ---
    // In demo mode the Sim Panel's market and city ARE the account's region, so
    // the settings page and the panel always agree: a save moves the sim.
    if (url === "/api/account/region" && (method === "GET" || method === "PUT")) {
      if (method === "PUT") {
        const parsed = accountRegionSchema.safeParse(readBody(init));
        if (!parsed.success) {
          return jsonResponse(
            errorEnvelope("VALIDATION_ERROR", parsed.error.issues[0]?.message ?? "Invalid region"),
            400,
          );
        }
        setSimContext(
          parsed.data.phRegion
            ? { market: parsed.data.market, region: parsed.data.phRegion }
            : { market: parsed.data.market },
        );
      }
      const ctx = getSimContext();
      return jsonResponse(
        successEnvelope({ market: ctx.market, phRegion: ctx.market === "ph" ? ctx.region : null }),
      );
    }

    // --- Organizations (demo runs in family context — no orgs) ---
    if (url === "/api/orgs" && method === "GET") {
      return jsonResponse(successEnvelope([]));
    }

    // --- Beta feedback: the landing form and the help menu's "Send feedback" ---
    // Mirrors the public POST /api/beta-feedback: the shared schema validates
    // (400 VALIDATION_ERROR on a bad body), then 201 { received: true }. The
    // demo keeps nothing; the real server stores the note with a masked page.
    if (url === "/api/beta-feedback" && method === "POST") {
      const parsed = createBetaFeedbackSchema.safeParse(readBody(init));
      if (!parsed.success) return validationError(parsed.error);
      return jsonResponse(successEnvelope({ received: true }), 201);
    }

    // --- Landing "Supported by" band: no sponsors in demo, so the band stays hidden ---
    if (url === "/api/sponsors" && method === "GET") {
      return jsonResponse(successEnvelope([]));
    }

    // --- Dashboard overview (at-a-glance stat row) ---
    if (url === "/api/dashboard/overview" && method === "GET") {
      // Same rules as the server: the count is the live, undeleted links, and
      // the medication-expiry fields exist only while health reminders are on.
      return jsonResponse(
        successEnvelope({
          activeLinkCount: demoSharedLinks.filter((l) => l.dismissedAt === null && isLiveDemoLink(l))
            .length,
          ...(HEALTH_REMINDERS_ENABLED
            ? { medicationsExpiringSoon: 1, nextMedicationExpiryDays: 12 }
            : {}),
        }),
      );
    }

    // --- Active links popup ---
    if (url === "/api/dashboard/links" && method === "GET") {
      return jsonResponse(successEnvelope(demoDashboardLinks()));
    }

    // --- Notifications (in-app center + dashboard feed + header bell) ---
    // Kinds switched off (the health reminders while HEALTH_REMINDERS_ENABLED
    // is off) are invisible to the feed, the count and both mark-read paths,
    // exactly as on the server; the rows stay in the demo store.
    if (url === "/api/notifications" && method === "GET") {
      const items = demoNotifications.filter((n) => isNotificationKindVisible(n.kind));
      const unreadCount = items.filter((n) => !n.readAt).length;
      return jsonResponse(successEnvelope({ items, unreadCount }));
    }
    if (url === "/api/notifications/read-all" && method === "POST") {
      let updated = 0;
      // Immutable per-row replacement (per coding-style.md CRITICAL rule).
      for (let i = 0; i < demoNotifications.length; i++) {
        const n = demoNotifications[i]!;
        if (!n.readAt && isNotificationKindVisible(n.kind)) {
          demoNotifications[i] = { ...n, readAt: now() };
          updated++;
        }
      }
      return jsonResponse(successEnvelope({ updated }));
    }
    const notifReadMatch = url.match(/^\/api\/notifications\/([^/]+)\/read$/);
    if (notifReadMatch && method === "PATCH") {
      const notifId = notifReadMatch[1]!;
      const index = demoNotifications.findIndex(
        (n) => n.id === notifId && isNotificationKindVisible(n.kind),
      );
      if (index === -1) {
        return jsonResponse(errorEnvelope("NOT_FOUND", "Notification not found"), 404);
      }
      const prev = demoNotifications[index]!;
      // Idempotent: keep the original readAt if already read.
      const marked: DemoNotification = { ...prev, readAt: prev.readAt ?? now() };
      demoNotifications[index] = marked;
      return jsonResponse(successEnvelope(marked));
    }

    // --- Notification preferences (settings page) ---
    // Like the server: every kind, missing = both channels on. The settings
    // page itself hides the switched-off kinds.
    if (url === "/api/notification-preferences" && (method === "GET" || method === "PUT")) {
      if (method === "PUT") {
        const body = readBody(init);
        for (const entry of Array.isArray(body) ? body : []) {
          const p = entry as Partial<DemoPreference>;
          if (typeof p.kind !== "string" || !DEMO_PREFERENCE_KINDS.includes(p.kind)) continue;
          demoPreferences[p.kind] = { inApp: p.inApp !== false, email: p.email !== false };
        }
      }
      const preferences = DEMO_PREFERENCE_KINDS.map((kind) => ({
        kind,
        inApp: demoPreferences[kind]?.inApp ?? true,
        email: demoPreferences[kind]?.email ?? true,
      }));
      return jsonResponse(successEnvelope({ preferences }));
    }

    // --- Children collection ---
    if (url === "/api/children" && method === "GET") {
      return jsonResponse(successEnvelope(demoChildren.map(withComputedAge)));
    }
    if (url === "/api/children" && method === "POST") {
      const raw = readBody(init);
      if (carriesPhoto(raw)) return jsonResponse(errorEnvelope("NOT_FOUND", "Not found"), 404);
      const body = raw as Partial<DemoChild>;
      const created: DemoChild = {
        id: randomId(),
        name: body.name ?? "Untitled",
        legalName: body.legalName ?? null,
        dateOfBirth: body.dateOfBirth ?? null,
        grade: body.grade ?? null,
        additionalNotes: body.additionalNotes ?? null,
        isEnrolled: body.isEnrolled ?? true,
        schoolName: body.schoolName ?? null,
        schoolPhone: body.schoolPhone ?? null,
        teacherName: body.teacherName ?? null,
        schoolNursePhone: body.schoolNursePhone ?? null,
        emergencyContactName: body.emergencyContactName ?? null,
        emergencyContactPhone: body.emergencyContactPhone ?? null,
        emergencyContactRelationship: body.emergencyContactRelationship ?? null,
        secondaryEmergencyContactName: body.secondaryEmergencyContactName ?? null,
        secondaryEmergencyContactPhone: body.secondaryEmergencyContactPhone ?? null,
        secondaryEmergencyContactRelationship:
          body.secondaryEmergencyContactRelationship ?? null,
        createdAt: now(),
        updatedAt: now(),
      };
      demoChildren.unshift(created);
      return jsonResponse(successEnvelope(withComputedAge(created)), 201);
    }

    // --- Public share resolver ---
    const publicShareMatch = url.match(/^\/api\/public\/share\/([^/]+)$/);
    if (publicShareMatch) {
      const token = publicShareMatch[1]!;
      const link = demoSharedLinks.find((l) => l.token === token);
      if (!link) {
        return jsonResponse(
          errorEnvelope(
            "LINK_NOT_FOUND",
            "This care profile link is invalid, expired, or no longer active.",
          ),
          404,
        );
      }
      // A deleted child's link answers exactly like a revoked one, as on the server.
      const child = demoChildren.find((c) => c.id === link.childId);
      if (!link.isActive || !child) {
        return jsonResponse(
          errorEnvelope("LINK_INACTIVE", "This link has been revoked."),
          410,
        );
      }
      if (link.expiresAt && new Date(link.expiresAt).getTime() <= Date.now()) {
        return jsonResponse(errorEnvelope("LINK_EXPIRED", "This link has expired."), 410);
      }
      // Immutable update: replace the row with a new object rather than
      // mutating in-place (per coding-style.md CRITICAL rule).
      const linkIdx = demoSharedLinks.findIndex((l) => l.id === link.id);
      if (linkIdx !== -1) {
        demoSharedLinks[linkIdx] = {
          ...link,
          accessCount: link.accessCount + 1,
          lastAccessedAt: now(),
        };
      }
      // Build scoped view using visibleSections.
      // Mirrors scopeStripper: only the sections the link opens today (no
      // `pickup` while Pickup & consent is off, and never pickup or consent
      // data: the demo carries none). The legal name rides with the nickname
      // ONLY when the link says `legalName: true` (strictly); there is never a
      // photoUrl.
      const sections = effectiveVisibleSections(link.visibleSections);
      const identity = sections.legalName === true ? { legalName: child.legalName } : {};
      interface ScopedView {
        visibleSections: DemoVisibleSections;
        child: {
          name: string;
          legalName?: string | null;
          age?: number | null;
          grade?: string | null;
          additionalNotes?: string | null;
          isEnrolled?: boolean;
          schoolName?: string | null;
          schoolPhone?: string | null;
          teacherName?: string | null;
          schoolNursePhone?: string | null;
        };
        emergencyContacts?: {
          primary: { name: string | null; phone: string | null; relationship: string | null };
          secondary: { name: string | null; phone: string | null; relationship: string | null };
        };
        allergies?: DemoAllergy[];
        medications?: DemoMedication[];
        carePreferences?: Omit<DemoCarePreferences, "id" | "childId" | "createdAt" | "updatedAt">;
        meta: { caregiverName: string; lastUpdated: string };
      }
      const scoped: ScopedView = {
        visibleSections: sections,
        child: sections.basic
          ? {
              name: child.name,
              ...identity,
              age: child.dateOfBirth ? calculateAge(child.dateOfBirth) : null,
              additionalNotes: child.additionalNotes,
              isEnrolled: child.isEnrolled,
              ...(child.isEnrolled
                ? {
                    grade: child.grade,
                    schoolName: child.schoolName,
                    schoolPhone: child.schoolPhone,
                    teacherName: child.teacherName,
                    schoolNursePhone: child.schoolNursePhone,
                  }
                : {}),
            }
          : { name: child.name, ...identity },
        meta: {
          caregiverName: link.caregiverName,
          lastUpdated: child.updatedAt,
        },
      };
      if (sections.emergencyContacts) {
        scoped.emergencyContacts = {
          primary: {
            name: child.emergencyContactName,
            phone: child.emergencyContactPhone,
            relationship: child.emergencyContactRelationship,
          },
          secondary: {
            name: child.secondaryEmergencyContactName,
            phone: child.secondaryEmergencyContactPhone,
            relationship: child.secondaryEmergencyContactRelationship,
          },
        };
      }
      if (sections.allergies) {
        scoped.allergies = demoAllergies.filter((a) => a.childId === child.id);
      }
      if (sections.medications) {
        scoped.medications = demoMedications.filter((m) => m.childId === child.id);
      }
      if (sections.carePreferences) {
        const prefs = demoCarePreferences.find((c) => c.childId === child.id);
        if (prefs) {
          scoped.carePreferences = {
            behavioralNotes: prefs.behavioralNotes,
            calmingStrategies: prefs.calmingStrategies,
            comfortItems: prefs.comfortItems,
            communicationStyle: prefs.communicationStyle,
          };
        }
      }
      return jsonResponse(successEnvelope(scoped));
    }

    // --- Authenticated shared-links CRUD ---
    const sharedLinksListMatch = url.match(/^\/api\/shared-links(\?.*)?$/);
    if (sharedLinksListMatch && method === "GET") {
      const urlObj = new URL(url, "http://localhost");
      const childId = urlObj.searchParams.get("childId");
      if (!childId) {
        return jsonResponse(
          errorEnvelope("VALIDATION_ERROR", "childId query param is required"),
          400,
        );
      }
      // Newest first, and never a link the parent deleted — like the server.
      const links = demoSharedLinks
        .filter((l) => l.childId === childId && l.dismissedAt === null)
        .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
        .map(toLinkDto);
      return jsonResponse(successEnvelope(links));
    }
    if (url === "/api/shared-links" && method === "POST") {
      const body = readBody(init) as {
        childId: string;
        caregiverName: string;
        caregiverEmail?: string | null;
        visibleSections: DemoVisibleSections;
        expirationPreset?: "24h" | "7d" | "30d" | "never";
      };
      const presetMs: Record<string, number> = {
        "24h": 24 * 60 * 60 * 1000,
        "7d": 7 * 24 * 60 * 60 * 1000,
        "30d": 30 * 24 * 60 * 60 * 1000,
      };
      const expiresAt =
        body.expirationPreset && body.expirationPreset !== "never"
          ? new Date(Date.now() + presetMs[body.expirationPreset]!).toISOString()
          : null;
      const token = randomToken();
      const created: DemoSharedLink = {
        id: randomId(),
        childId: body.childId,
        token,
        tokenPrefix: token.slice(0, 6),
        caregiverName: body.caregiverName,
        caregiverEmail: body.caregiverEmail ?? null,
        // Like SharedLinksService.create: stored `pickup: false` while off.
        visibleSections: PICKUP_CONSENT_ENABLED
          ? body.visibleSections
          : { ...body.visibleSections, pickup: false },
        expiresAt,
        isActive: true,
        lastAccessedAt: null,
        accessCount: 0,
        createdAt: now(),
        revokedAt: null,
        acknowledgedAt: null,
        dismissedAt: null,
      };
      demoSharedLinks.unshift(created);
      return jsonResponse(successEnvelope({ link: toLinkDto(created), token }), 201);
    }

    // "Delete" in the Active links popup: revoke if still active, then hide.
    // Mirrors POST /api/shared-links/:id/dismiss (404 when missing or already
    // deleted; an earlier cancel time is kept).
    const sharedLinkDismissMatch = url.match(/^\/api\/shared-links\/([^/]+)\/dismiss$/);
    if (sharedLinkDismissMatch && method === "POST") {
      const linkId = sharedLinkDismissMatch[1]!;
      const idx = demoSharedLinks.findIndex((l) => l.id === linkId && l.dismissedAt === null);
      if (idx === -1) {
        return jsonResponse(errorEnvelope("NOT_FOUND", "Shared link not found"), 404);
      }
      const prev = demoSharedLinks[idx]!;
      demoSharedLinks[idx] = {
        ...prev,
        isActive: false,
        revokedAt: prev.revokedAt ?? now(),
        dismissedAt: now(),
      };
      return jsonResponse(successEnvelope({ dismissed: true }));
    }

    const sharedLinkItemMatch = url.match(/^\/api\/shared-links\/([^/]+)$/);
    if (sharedLinkItemMatch && method === "DELETE") {
      const linkId = sharedLinkItemMatch[1]!;
      // Like the server: only a link that is still active can be cancelled.
      const idx = demoSharedLinks.findIndex(
        (l) => l.id === linkId && l.isActive && l.dismissedAt === null,
      );
      if (idx === -1) {
        return jsonResponse(errorEnvelope("NOT_FOUND", "Shared link not found"), 404);
      }
      // Immutable replacement (per coding-style.md CRITICAL rule).
      demoSharedLinks[idx] = {
        ...demoSharedLinks[idx]!,
        isActive: false,
        revokedAt: now(),
      };
      return jsonResponse(successEnvelope({ revoked: true }));
    }

    const sharedLinkEventsMatch = url.match(/^\/api\/shared-links\/([^/]+)\/events$/);
    if (sharedLinkEventsMatch && method === "GET") {
      // No real event history in demo mode — just return a synthetic sample
      const linkId = sharedLinkEventsMatch[1]!;
      const link = demoSharedLinks.find((l) => l.id === linkId && l.dismissedAt === null);
      if (!link) {
        return jsonResponse(errorEnvelope("NOT_FOUND", "Shared link not found"), 404);
      }
      const events = Array.from({ length: link.accessCount }, (_, i) => ({
        id: i + 1,
        accessedAt: new Date(Date.now() - (i + 1) * 60 * 60 * 1000).toISOString(),
        action: "resolve" as const,
        httpStatus: 200,
      }));
      return jsonResponse(successEnvelope(events));
    }

    // --- Care preferences ---
    const careMatch = url.match(/^\/api\/care\/([^/]+)$/);
    if (careMatch) {
      const childId = careMatch[1]!;
      const index = demoCarePreferences.findIndex((c) => c.childId === childId);
      if (method === "GET") {
        return jsonResponse(
          successEnvelope(index === -1 ? null : demoCarePreferences[index]),
        );
      }
      if (method === "PUT") {
        const body = readBody(init) as Partial<DemoCarePreferences>;
        if (index === -1) {
          const created: DemoCarePreferences = {
            id: randomId(),
            childId,
            behavioralNotes: body.behavioralNotes ?? null,
            calmingStrategies: body.calmingStrategies ?? null,
            comfortItems: body.comfortItems ?? null,
            communicationStyle: body.communicationStyle ?? null,
            createdAt: now(),
            updatedAt: now(),
          };
          demoCarePreferences.push(created);
          return jsonResponse(successEnvelope(created));
        }
        const prev = demoCarePreferences[index]!;
        const updated: DemoCarePreferences = {
          ...prev,
          behavioralNotes:
            body.behavioralNotes !== undefined ? body.behavioralNotes : prev.behavioralNotes,
          calmingStrategies:
            body.calmingStrategies !== undefined
              ? body.calmingStrategies
              : prev.calmingStrategies,
          comfortItems:
            body.comfortItems !== undefined ? body.comfortItems : prev.comfortItems,
          communicationStyle:
            body.communicationStyle !== undefined
              ? body.communicationStyle
              : prev.communicationStyle,
          updatedAt: now(),
        };
        demoCarePreferences[index] = updated;
        return jsonResponse(successEnvelope(updated));
      }
    }

    // --- Nested allergies routes (must come BEFORE single child match) ---
    const allergyListMatch = url.match(/^\/api\/children\/([^/]+)\/allergies$/);
    if (allergyListMatch) {
      const childId = allergyListMatch[1]!;
      if (method === "GET") {
        return jsonResponse(
          successEnvelope(demoAllergies.filter((a) => a.childId === childId)),
        );
      }
      if (method === "POST") {
        const body = readBody(init) as Partial<DemoAllergy>;
        const created: DemoAllergy = {
          id: randomId(),
          childId,
          name: body.name ?? "Untitled",
          severity: (body.severity ?? "mild") as DemoSeverity,
          description: body.description ?? null,
          emergencyAction: body.emergencyAction ?? null,
          reactionSymptoms: body.reactionSymptoms ?? null,
          treatment: body.treatment ?? null,
          epinephrineLocation: body.epinephrineLocation ?? null,
          rescueMedicationId: body.rescueMedicationId ?? null,
          notes: body.notes ?? null,
          createdAt: now(),
          updatedAt: now(),
        };
        demoAllergies.unshift(created);
        return jsonResponse(successEnvelope(created), 201);
      }
    }

    const allergyItemMatch = url.match(/^\/api\/children\/([^/]+)\/allergies\/([^/]+)$/);
    if (allergyItemMatch) {
      const childId = allergyItemMatch[1]!;
      const allergyId = allergyItemMatch[2]!;
      const index = demoAllergies.findIndex((a) => a.id === allergyId && a.childId === childId);
      if (method === "PUT") {
        if (index === -1) return jsonResponse(errorEnvelope("NOT_FOUND", "Allergy not found"), 404);
        const body = readBody(init) as Partial<DemoAllergy>;
        const prev = demoAllergies[index]!;
        const updated: DemoAllergy = {
          ...prev,
          ...body,
          id: prev.id,
          childId: prev.childId,
          updatedAt: now(),
        };
        demoAllergies[index] = updated;
        return jsonResponse(successEnvelope(updated));
      }
      if (method === "DELETE") {
        if (index === -1) return jsonResponse(errorEnvelope("NOT_FOUND", "Allergy not found"), 404);
        demoAllergies.splice(index, 1);
        return jsonResponse(successEnvelope({ deleted: true }));
      }
    }

    // --- Nested medications routes ---
    const medicationListMatch = url.match(/^\/api\/children\/([^/]+)\/medications$/);
    if (medicationListMatch) {
      const childId = medicationListMatch[1]!;
      if (method === "GET") {
        return jsonResponse(
          successEnvelope(demoMedications.filter((m) => m.childId === childId)),
        );
      }
      if (method === "POST") {
        const body = readBody(init) as Partial<DemoMedication>;
        const created: DemoMedication = {
          id: randomId(),
          childId,
          name: body.name ?? "Untitled",
          dosage: body.dosage ?? "",
          frequency: (body.frequency ?? "as_needed") as DemoFrequency,
          expiresAt: body.expiresAt ?? null,
          notes: body.notes ?? null,
          createdAt: now(),
          updatedAt: now(),
        };
        demoMedications.unshift(created);
        return jsonResponse(successEnvelope(created), 201);
      }
    }

    const medicationItemMatch = url.match(/^\/api\/children\/([^/]+)\/medications\/([^/]+)$/);
    if (medicationItemMatch) {
      const childId = medicationItemMatch[1]!;
      const medicationId = medicationItemMatch[2]!;
      const index = demoMedications.findIndex(
        (m) => m.id === medicationId && m.childId === childId,
      );
      if (method === "PUT") {
        if (index === -1)
          return jsonResponse(errorEnvelope("NOT_FOUND", "Medication not found"), 404);
        const body = readBody(init) as Partial<DemoMedication>;
        const prev = demoMedications[index]!;
        const updated: DemoMedication = {
          ...prev,
          ...body,
          id: prev.id,
          childId: prev.childId,
          updatedAt: now(),
        };
        demoMedications[index] = updated;
        return jsonResponse(successEnvelope(updated));
      }
      if (method === "DELETE") {
        if (index === -1)
          return jsonResponse(errorEnvelope("NOT_FOUND", "Medication not found"), 404);
        demoMedications.splice(index, 1);
        return jsonResponse(successEnvelope({ deleted: true }));
      }
    }

    // --- Child photo (PUT refused / DELETE allowed) ---
    // Must precede the single-child match. Mirrors the real server while
    // PHOTO_UPLOADS_ENABLED is off: uploading answers the standard 404 (the
    // route isn't mounted), removing always works. The demo stores no photos.
    const childPhotoMatch = url.match(/^\/api\/children\/([^/]+)\/photo$/);
    if (childPhotoMatch) {
      if (method !== "DELETE") {
        return jsonResponse(errorEnvelope("NOT_FOUND", "Not found"), 404);
      }
      const childId = childPhotoMatch[1]!;
      const index = demoChildren.findIndex((c) => c.id === childId);
      if (index === -1) return jsonResponse(errorEnvelope("NOT_FOUND", "Child not found"), 404);
      const updated: DemoChild = { ...demoChildren[index]!, updatedAt: now() };
      demoChildren[index] = updated;
      return jsonResponse(successEnvelope(withComputedAge(updated)));
    }

    // --- Immunizations, medical history, routines, feeding, pickups, consent ---
    // Reads and writes, starting empty (see handleChildRecordsMock). This
    // replaced the earlier GET-only empty-state answers for the same paths.
    const recordsResponse = handleChildRecordsMock(url, method, init);
    if (recordsResponse) return recordsResponse;

    // --- Single child routes ---
    const singleMatch = url.match(/^\/api\/children\/([^/]+)$/);
    if (singleMatch) {
      const childId = singleMatch[1]!;
      const index = demoChildren.findIndex((c) => c.id === childId);

      if (method === "GET") {
        if (index === -1) return jsonResponse(errorEnvelope("NOT_FOUND", "Child not found"), 404);
        return jsonResponse(successEnvelope(withComputedAge(demoChildren[index]!)));
      }
      if (method === "PUT") {
        if (index === -1) return jsonResponse(errorEnvelope("NOT_FOUND", "Child not found"), 404);
        const raw = readBody(init);
        if (carriesPhoto(raw)) return jsonResponse(errorEnvelope("NOT_FOUND", "Not found"), 404);
        const body = raw as Partial<DemoChild>;
        const prev = demoChildren[index]!;
        const updated: DemoChild = { ...prev, ...body, updatedAt: now(), id: prev.id };
        demoChildren[index] = updated;
        return jsonResponse(successEnvelope(withComputedAge(updated)));
      }
      if (method === "DELETE") {
        if (index === -1) return jsonResponse(errorEnvelope("NOT_FOUND", "Child not found"), 404);
        demoChildren.splice(index, 1);
        // Like the server: deleting a child revokes every care link still active for it.
        const revokedAt = now();
        demoSharedLinks.forEach((l, i) => {
          if (l.childId === childId && l.isActive) {
            demoSharedLinks[i] = { ...l, isActive: false, revokedAt };
          }
        });
        return jsonResponse(successEnvelope({ deleted: true }));
      }
    }

    // Unknown route — fall through to real fetch (will probably 404).
    return realFetch(input, init);
  };

  // Log once on load so it's obvious the interceptor is active.
  // eslint-disable-next-line no-console
  console.info("[LeenasList] Demo mode active — API calls are mocked client-side.");
}

export const IS_DEMO_MODE = Boolean(import.meta.env.VITE_DEMO_MODE);
